import { cn } from '../../lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Edit, Loader2 } from 'lucide-react';

export function DailyDetailTable({ loading, records, onEdit }) {
  return (
    <div className="glass-card table-scroll">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Shift Template</TableHead>
              <TableHead>Shift Type</TableHead>
              <TableHead>Shift Date</TableHead>
              <TableHead>Day</TableHead>
              <TableHead>Clock In</TableHead>
              <TableHead>Clock Out</TableHead>
              <TableHead>Late Deduction</TableHead>
              <TableHead>Day Shift</TableHead>
              <TableHead>Night Shift</TableHead>
              <TableHead>Overtime</TableHead>
              <TableHead>Night OT</TableHead>
              <TableHead>Annual Leave</TableHead>
              <TableHead>Sick Leave</TableHead>
              <TableHead>Family Resp.</TableHead>
              <TableHead>Unpaid Leave</TableHead>
              <TableHead>Absent</TableHead>
              <TableHead>Total Payable</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={19} className="text-center py-10 text-muted-foreground">
                  No attendance records found
                </TableCell>
              </TableRow>
            ) : (
              records.map((record, idx) => (
                <TableRow key={`${record.employee_id}-${record.shift_date || record.date}-${idx}`}>
                  <TableCell>
                    <div>
                      <span className="font-medium flex items-center gap-1">
                        {record.employee_name}
                        {record.is_corrected && (
                          <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] px-1 py-0">
                            corrected
                          </Badge>
                        )}
                      </span>
                      <p className="text-xs text-muted-foreground">{record.department}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {record.shift_template_name ? (
                      <Badge variant="outline" className="bg-purple-500/15 text-purple-300 border-purple-500/30 text-xs">
                        {record.shift_template_name}
                      </Badge>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        record.shift_type === 'Night Shift'
                          ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                          : 'bg-sky-500/20 text-sky-300 border-sky-500/30'
                      )}
                    >
                      {record.shift_type || 'Day Shift'}
                    </Badge>
                  </TableCell>
                  <TableCell>{record.shift_date || record.date}</TableCell>
                  <TableCell>{record.day_of_week}</TableCell>
                  <TableCell className={cn(!record.first_clock_in && "text-muted-foreground")}>
                    {record.first_clock_in?.slice(0, 5) || '-'}
                    {record.first_clock_in_date && record.first_clock_in_date !== (record.shift_date || record.date) && (
                      <span className="ml-1 text-[10px] text-muted-foreground">({record.first_clock_in_date})</span>
                    )}
                  </TableCell>
                  <TableCell className={cn(!record.last_clock_out && "text-muted-foreground")}>
                    {record.last_clock_out?.slice(0, 5) || '-'}
                    {record.last_clock_out_date && record.last_clock_out_date !== (record.shift_date || record.date) && (
                      <span className="ml-1 text-[10px] text-indigo-300">(+1d {record.last_clock_out_date})</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {record.late_deduction_minutes > 0 ? (
                      <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                        {record.late_deduction_minutes} min
                      </Badge>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.day_shift_hours ?? 0) > 0 ? (
                      <span className="text-sky-400">{record.day_shift_hours.toFixed(2)}h</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.night_shift_hours ?? 0) > 0 ? (
                      <span className="text-indigo-400">{record.night_shift_hours.toFixed(2)}h</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.overtime_hours ?? 0) > 0 ? (
                      <span className="text-orange-400">{record.overtime_hours.toFixed(2)}h</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.night_overtime_hours ?? 0) > 0 ? (
                      <span className="text-indigo-400" title="Night hours worked as overtime (night premium)" data-testid={`night-ot-${record.employee_id}-${record.shift_date}`}>
                        {record.night_overtime_hours.toFixed(2)}h
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.paid_annual_leave_hours ?? 0) > 0 ? (
                      <span className="text-emerald-400" data-testid={`annual-leave-${record.employee_id}-${record.shift_date}`}>
                        {record.paid_annual_leave_hours.toFixed(2)}h
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.paid_sick_leave_hours ?? 0) > 0 ? (
                      <span className="text-rose-400" data-testid={`sick-leave-${record.employee_id}-${record.shift_date}`}>
                        {record.paid_sick_leave_hours.toFixed(2)}h
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.paid_family_leave_hours ?? 0) > 0 ? (
                      <span className="text-purple-400" data-testid={`family-leave-${record.employee_id}-${record.shift_date}`}>
                        {record.paid_family_leave_hours.toFixed(2)}h
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.unpaid_leave_hours ?? 0) > 0 ? (
                      <span className="text-slate-400">{record.unpaid_leave_hours.toFixed(2)}h</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {(record.absent_unpaid_hours ?? 0) > 0 ? (
                      <span className="text-red-400" data-testid={`absent-${record.employee_id}-${record.shift_date}`}>
                        {record.absent_unpaid_hours.toFixed(2)}h
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="font-semibold">
                    {(record.total_payable_hours ?? 0) > 0 ? (
                      <span className="text-foreground">{record.total_payable_hours.toFixed(2)}h</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn(
                      record.status === 'Complete' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                      record.status === 'On Leave' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                      record.status === 'Absent' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    )}>
                      {record.leave_status || record.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => onEdit(record)} data-testid={`edit-${record.employee_id}-${record.shift_date || record.date}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
